// GES-16 Phase 3 — FIXED ORDER DISCOUNT panel (brief 45046 §8.1). Hosted by the order-detail action panel.
// Model: SaleFeesPanel (style tokens copied per the inline-panel convention; admin control ABSENT, not
// disabled, for non-admins). Every money figure is the API's exact text — the browser computes nothing (R25):
//   POST /api/order-discounts/preview  (debounced) · POST /api/order-discounts · GET /api/order-discounts/:op
//   POST /api/order-discounts/:id/void (admin).
import MoneyTextInput from '../MoneyTextInput';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { DrawerActions } from '../../shell/DrawerBody';
import { useAuth } from '../../context/AuthContext';
import { formatDate, formatMoneyExact } from '../../utils/format';
import { numberText } from '../../pages/PriceList/priceListFormat';
import { discountErrorText } from './discountDisplay.mjs';
import { useCreditGate } from './CreditLimitNotice';   // GES-15: the admin void is credit door #11
import { openCreditNote } from '../sale/OverInvoicedBanner';
import { newIdempotencyKey } from './creditNoteHelpers.mjs';
import AvoirOffer from './avoir/AvoirOffer';
import IssueNowCheck from './avoir/IssueNowCheck';   // the Discount shortcut 'Also issue the credit note now'   // AVOIR Phase 4.2: the offered draft credit note (only when the API sends it)
import './SaleDiscountPanel.css';
import { TextInput } from '../inputs/Input';







const todayStr = () => new Date().toISOString().slice(0, 10);
export const moneyF = formatMoneyExact;   // GES-406: exact amount, currency label from Settings ▸ Formatting

// onFooter: when given (the discount door's OperationDrawer), the panel renders NO footer of its own and reports { canSubmit, busy, invoiced, submit } so the drawer's footer carries the ONE primary.
export default function SaleDiscountPanel({ operationId, customerId = '', isCash = false, onChanged = () => {}, onClose = () => {}, onFooter = null }) {
  const { t } = useTranslation('sales'); const { t: tc } = useTranslation('common');
  const tcRef = useRef(tc); tcRef.current = tc;   // latest tc without making loadList (and the effect that calls it) re-run when t changes identity
  const { isAdmin } = useAuth();
  const [idemKey, setIdemKey] = useState(newIdempotencyKey);   // W13 (R-N): vouchers are no longer offered here: they are applied from the order's 'Apply customer money / credit' door
  const [mode, setMode] = useState('fixed');
  const [value, setValue] = useState('');
  const [date, setDate] = useState(todayStr());
  const [notes, setNotes] = useState('');
  const [issueCn, setIssueCn] = useState(false);
  const [preview, setPreview] = useState(null);       // { data } | { error } | 'loading'
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState(null);
  const [created, setCreated] = useState(null);       // { warning } after a 201 (warning = API text or null)
  const [list, setList] = useState(null);             // GET /api/order-discounts/:op body
  const [listErr, setListErr] = useState(null);
  const [voidFor, setVoidFor] = useState(null);
  const [voidBusy, setVoidBusy] = useState(false);
  const [voidErr, setVoidErr] = useState(null);
  const credit = useCreditGate();


  const errText = (r) => discountErrorText(t, r.data, r.status, moneyF);
  const raw = value.trim().replace(',', '.');
  const body = mode === 'fixed' ? { operation_id: operationId, mode, amount: raw } : { operation_id: operationId, mode, pct: raw };


  const loadList = useCallback(() => {
    setList(null); setListErr(null);
    api.getOrderDiscounts(operationId).then(setList).catch(e => setListErr(tcRef.current('requestFailed')));
  }, [operationId]);
  useEffect(() => { loadList(); }, [loadList]);

  // Live preview, debounced; writes nothing. A stale response never overwrites a newer input.
  useEffect(() => {
    if (!raw) { setPreview(null); return undefined; }
    let live = true;
    setPreview('loading');
    const h = setTimeout(() => {
      api.previewOrderDiscount(body)
        .then(r => { if (live) setPreview(r.ok ? { data: r.data } : { error: errText(r) }); })
        .catch(e => { if (live) setPreview({ error: tc('requestFailed') }); });
    }, 400);
    return () => { live = false; clearTimeout(h); };
  }, [mode, raw, operationId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function submit() {
    setSubmitting(true); setSubmitErr(null); setCreated(null);
    try {
      const r = await api.createOrderDiscount({ ...body, date, notes: notes.trim() || null, ...(issueCn ? { issue_credit_note: true, credit_note_date: date } : {}), idempotency_key: idemKey });   // 47001 W2: ONE key per open form on every discount create (the API replays instead of writing twice)
      if (!r.ok) { setSubmitErr(errText(r)); return; }
      // 201 is success even with DISCOUNT_CREATED_WITH_WARNING: the discount committed — never re-submit.
      setCreated({ warning: r.data?.warning?.message || null, data: r.data });
      setValue(''); setNotes(''); setPreview(null);
      setIdemKey(newIdempotencyKey());
      loadList(); onChanged();
    } catch (e) { setSubmitErr(tc('requestFailed')); } finally { setSubmitting(false); }
  }

  async function doVoid(id, extra = {}) {
    setVoidBusy(true); setVoidErr(null); credit.clear();
    try {
      const r = await api.voidOrderDiscount(id, extra);
      if (!r.ok && credit.catchCredit(r.data, (ov) => doVoid(id, ov))) return;
      if (!r.ok) { setVoidErr(errText(r)); return; }
      setVoidFor(null); loadList(); onChanged();
    } catch (e) { setVoidErr(tc('requestFailed')); } finally { setVoidBusy(false); }
  }

  const p = preview && preview !== 'loading' ? preview : null;
  const rows = list?.discounts || [];
  const snap = list?.status_snapshot || null;   // Block 1: the server's NET figures + the invoiced notice
  const invoiced = !!snap?.caps?.discount_needs_credit_note;   // an issued invoice covers the order: a discount is a credit note, never a silent order discount
  const delivered = snap ? Number(snap.money.remaining_to_deliver) <= 0.005 : false;
  const canSubmit = !submitting && !!raw && !invoiced;
  useEffect(() => { if (onFooter) onFooter({ canSubmit, busy: submitting, invoiced, hasValue: !!raw, submit }); });   // eslint-disable-line react-hooks/exhaustive-deps -- reports every render: the drawer footer must hold the latest submit closure

  return (
    <div>
      <div className="sdp-1">{t(delivered ? 'discount.introDelivered' : 'discount.intro')}</div>
      {invoiced && (
        <div className="sdp-2" data-testid="discount-invoiced-notice">
          <span className="sdp-3">{t('discount.invoicedNotice')}</span>
          <button type="button" className="btn" data-testid="discount-credit-note" onClick={() => openCreditNote(operationId, '', '', '')}>{t('discount.issueCreditNote')}</button>
        </div>
      )}
      {isCash && <div className="sdp-4">{t('discount.cashHint')}</div>}

      {/* ── existing discounts ── */}
      <div className="sdp-5">{t('discount.listTitle')}</div>
      {list === null && !listErr && <div className="sdp-6">{t('discount.loading')}</div>}
      {listErr && (
        <div className="sdp-7">
          <span>{t('discount.loadErr', { msg: listErr })}</span>
          <button type="button" className="btn sdp-8" onClick={loadList}>{t('discount.retry')}</button>
        </div>
      )}
      {list && rows.length === 0 && <div className="sdp-1">{t('discount.empty')}</div>}
      {rows.length > 0 && (
        <div className="sdp-9">
          <table className="gtable zebra sdp-10">
            <thead><tr>
              {isAdmin && <th className="sdp-11">{t('discount.colActions')}</th>}
              <th className="sdp-12">{t('discount.colAmount')}</th>
              <th className="sdp-12">{t('discount.colRecognised')}</th>
              <th className="sdp-12">{t('discount.colRemainder')}</th>
              <th className="sdp-11">{t('discount.colState')}</th>
              <th className="sdp-11">{t('discount.colDate')}</th>
            </tr></thead>
            <tbody>
              {rows.map(d => (
                <tr key={d.id}>
                  {/* ADMIN ONLY, and ABSENT rather than disabled (SaleFeesPanel's Cancel-fee idiom). */}
                  {isAdmin && (
                    <td className="sdp-11">
                      {d.state === 'open' && (
                        <button type="button" className="btn sm sdp-13"
                          onClick={() => { setVoidFor(voidFor === d.id ? null : d.id); setVoidErr(null); }}>
                          {voidFor === d.id ? t('discount.close') : t('discount.voidRemainder')}
                        </button>
                      )}
                      {voidFor === d.id && (
                        <button type="button" className="btn sm danger sdp-void"
                          disabled={voidBusy} title={voidBusy ? t('discount.voiding') : undefined} onClick={() => doVoid(d.id)}>
                          {voidBusy ? t('discount.voiding') : t('discount.confirmVoid')}
                        </button>
                      )}
                    </td>
                  )}
                  <td className="sdp-14">{moneyF(d.amount)}{d.entry_mode === 'percent' && d.entry_pct ? ` (${numberText(d.entry_pct)} %)` : ''}</td>
                  <td className="sdp-14">{moneyF(d.recognised)}</td>
                  <td className="sdp-14">{moneyF(d.remainder)}</td>
                  <td className="sdp-11" title={d.void_reason ? t('discount.voidedTitle', { amount: moneyF(d.voided_amount), reason: d.void_reason }) : undefined}>
                    {t(`discount.state.${d.state}`, { defaultValue: d.state })}
                  </td>
                  <td className="sdp-11">{formatDate(d.date)}{d.notes ? ` · ${d.notes}` : ''}</td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr className="sdp-15">
              {isAdmin && <td className="sdp-11" />}
              <td className="sdp-14" title={t('discount.committedTitle')}>{moneyF(list.committed_total)}</td>
              <td className="sdp-14">{moneyF(list.recognised_total)}</td>
              <td className="sdp-11" colSpan={3}>{t('discount.total')}</td>
            </tr></tfoot>
          </table>
        </div>
      )}
      {credit.notice}
      {voidErr && <div className="sdp-16">{voidErr}</div>}
      {(rows.length > 0 || (snap && list.order_value != null)) && (
        <div className="sdp-17" data-testid="discount-net">
          <span>{t('discount.orderValue')}: <b className="sdp-18">{moneyF(list.order_value)}</b></span>
          {rows.length > 0 && <span>{t('discount.deliveredValue')}: <b className="sdp-18">{moneyF(list.delivered_value)}</b></span>}
          {rows.length > 0 && <span>{t('discount.netDueDelivered')}: <b className="sdp-18">{moneyF(list.net_due_delivered)}</b></span>}
          <span>{t('discount.maxAdditional')}: <b className="sdp-18">{moneyF(list.max_additional)}</b></span>
        </div>
      )}

      {/* ── add form (absent on an invoiced order: that is a credit note) ── */}
      {!invoiced && <div className="sdp-19">
        <div className="sdp-5">{t('discount.add')}</div>
        <div className="sdp-20">
          <div className="sdp-21">
            <button type="button" className={`sdp-seg${mode === 'fixed' ? ' on' : ''}`} onClick={() => setMode('fixed')}>{t('discount.modeFixed')}</button>
            <button type="button" className={`sdp-seg${mode === 'percent' ? ' on' : ''}`} onClick={() => setMode('percent')}>{t('discount.modePercent')}</button>
          </div>
        </div>
        <div className="sdp-20">
          <label className="sdp-22">
            <span className="sdp-23">{mode === 'fixed' ? t('discount.amount') : t('discount.percent')} *</span>
            <MoneyTextInput value={value} onChange={e => setValue(e.target.value)} placeholder="0" className="sdp-24" />
          </label>
          <label className="sdp-25">
            <span className="sdp-23">{t('discount.date')} *</span>
            <TextInput type="date" value={date} onChange={e => setDate(e.target.value)} className="sdp-24" />
          </label>
        </div>

        {/* live preview */}
        {preview === 'loading' && <div className="sdp-1">{t('discount.previewing')}</div>}
        {p?.error && <div className="sdp-16">{p.error}</div>}
        {p?.data && (
          <div className={`sdp-preview${p.data.would_exceed ? ' bad' : ''}`}>
            <span>{t('discount.resulting')}: <b>{moneyF(p.data.amount)}</b></span>
            <span>{t('discount.orderValue')}: <b>{moneyF(p.data.order_value)}</b></span>
            <span>{t('discount.maxAdditional')}: <b>{moneyF(p.data.max_additional)}</b></span>
            {p.data.would_exceed && (
              <span className="sdp-30">
                {p.data.code ? discountErrorText(t, p.data, null, moneyF) : t('discount.wouldExceed', { max: moneyF(p.data.max_additional) })}
              </span>
            )}
          </div>
        )}

        <div className="sdp-26">
          <span className="sdp-23">{t('discount.notes')}</span>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder={t('discount.notesPh')}
            className="sdp-31" />
        </div>

        <IssueNowCheck operationId={operationId} checked={issueCn} onChange={setIssueCn} />

        {submitErr && <div className="sdp-16">{submitErr}</div>}
        {created && (
          <div className={`sdp-created${created.warning ? ' warn' : ''}`}>
            {t('discount.added')}{created.warning ? <> {t('discount.createdWithWarning')} {created.warning}</> : null}
          </div>
        )}
        {created && <AvoirOffer data={created.data} onChanged={() => { loadList(); onChanged(); }} />}
        {!onFooter && <DrawerActions closeFirst legacyRow="act-row split pt14"
          legacyClose={<button type="button" onClick={onClose} className="btn" disabled={submitting}>{t('discount.close')}</button>}>
          {inFoot => (
            <button type="button" onClick={submit} className="btn success" disabled={!canSubmit} title={canSubmit ? undefined : (submitting ? t('discount.submitting') : invoiced ? t('discount.invoicedNotice') : t('discount.needValue'))}>
              {submitting ? t('discount.submitting') : t('discount.submit')}
            </button>
          )}
        </DrawerActions>}
      </div>}
      {invoiced && !onFooter && <div className="sdp-33"><button type="button" onClick={onClose} className="btn">{t('discount.close')}</button></div>}
    </div>
  );
}
